import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { findUseCasePage, USE_CASE_PAGES, type UseCaseSlug } from '../../marketing/use-case-pages';
import { Button } from '../ui/button';
import { Footer } from './Footer';
import { trackCtaClicked } from '../../utils/analytics';

interface UseCasePageProps {
  slug: UseCaseSlug;
}

export const UseCasePage: React.FC<UseCasePageProps> = ({ slug }) => {
  const { t } = useLocale();
  const page = findUseCasePage(`/${slug}`);

  const logoUrl = useMemo(
    () =>
      new URL(
        '../../assets/logos/SVG/POSITIVO/logo scribane_positivo completo h adjusted.svg',
        import.meta.url,
      ).toString(),
    [],
  );

  if (!page) {
    return null;
  }

  const prefix = page.keyPrefix;
  const benefits = Array.from({ length: page.benefitCount }, (_, index) => ({
    title: t(`${prefix}_benefit_${index + 1}_title`),
    body: t(`${prefix}_benefit_${index + 1}_body`),
  }));

  const otherPages = USE_CASE_PAGES.filter((entry) => entry.slug !== slug);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-8">
          <Link to="/" className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
            <img src={logoUrl} alt="Scribane" className="h-8 min-[864px]:h-12 w-auto" />
          </Link>
          <Button asChild variant="inverse">
            <Link to="/register" onClick={() => trackCtaClicked(t('landing_cta_start'), `use_case_${slug}`)}>
              {t('landing_cta_start')}
            </Link>
          </Button>
        </div>
      </header>

      <main className="bg-white">
        <section style={{ padding: '72px 0 40px' }}>
          <div style={{ width: '100%', maxWidth: '860px', margin: '0 auto', padding: '0 32px' }}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[rgba(39,39,41,0.65)] hover:text-[rgb(39,39,41)] mb-10"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('use_case_back_home')}
            </Link>

            <p
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '3px',
                textTransform: 'uppercase',
                color: 'var(--scribane-green)',
                margin: '0 0 18px',
              }}
            >
              {t(`${prefix}_eyebrow`)}
            </p>

            <h1
              style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 'clamp(34px, 5vw, 56px)',
                lineHeight: 1.08,
                letterSpacing: '-0.02em',
                margin: '0 0 22px',
                color: 'rgb(39,39,41)',
              }}
            >
              {t(`${prefix}_h1`)}
            </h1>

            <p
              style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: '19px',
                lineHeight: '30px',
                color: 'rgba(39,39,41,0.75)',
                margin: '0 0 28px',
              }}
            >
              {t(`${prefix}_lead`)}
            </p>

            <Button
              asChild
              className="rounded-[10px] py-[11px] px-5 h-auto text-[14px] bg-[var(--scribane-green)] text-[var(--scribane-black)] hover:bg-[var(--scribane-black)] hover:text-[var(--scribane-green)]"
            >
              <Link to="/register" onClick={() => trackCtaClicked(t(`${prefix}_cta`), `use_case_${slug}_hero`)}>
                {t(`${prefix}_cta`)}
              </Link>
            </Button>
          </div>
        </section>

        <section style={{ padding: '40px 0 80px' }}>
          <div style={{ width: '100%', maxWidth: '860px', margin: '0 auto', padding: '0 32px' }}>
            <h2
              style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: '28px',
                fontWeight: 700,
                margin: '0 0 28px',
                color: 'rgb(39,39,41)',
              }}
            >
              {t(`${prefix}_benefits_title`)}
            </h2>

            <div className="grid grid-cols-1 gap-5 min-[800px]:grid-cols-2">
              {benefits.map((benefit) => (
                <article
                  key={benefit.title}
                  style={{
                    border: '1px solid #e7e7e8',
                    borderRadius: '16px',
                    padding: '26px 24px',
                    background: '#fff',
                  }}
                >
                  <h3
                    style={{
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: '18px',
                      fontWeight: 700,
                      margin: '0 0 12px',
                      color: 'rgb(39,39,41)',
                    }}
                  >
                    {benefit.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: '15px',
                      lineHeight: '26px',
                      color: '#686869',
                      margin: 0,
                    }}
                  >
                    {benefit.body}
                  </p>
                </article>
              ))}
            </div>

            <p
              style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: '17px',
                lineHeight: '28px',
                color: 'rgba(39,39,41,0.78)',
                margin: '40px 0 0',
              }}
            >
              {t(`${prefix}_closing`)}
            </p>
          </div>
        </section>

        <section style={{ padding: '0 0 96px' }}>
          <div style={{ width: '100%', maxWidth: '860px', margin: '0 auto', padding: '0 32px' }}>
            <h2
              style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: '22px',
                fontWeight: 700,
                margin: '0 0 18px',
                color: 'rgb(39,39,41)',
              }}
            >
              {t('use_case_also_for')}
            </h2>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {otherPages.map((other) => (
                <li key={other.slug}>
                  <Link
                    to={other.path}
                    className="text-[var(--scribane-black)] underline underline-offset-4 hover:text-[var(--scribane-green)]"
                  >
                    {t(`${other.keyPrefix}_nav_label`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};
